export type ApiErrorPayload = {
  code?: string;
  message?: string;
  retryAfterSec?: number;
  detail?: unknown;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  retryAfterSec?: number;
  payload?: ApiErrorPayload;

  constructor(status: number, message: string, payload?: ApiErrorPayload) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = payload?.code;
    this.retryAfterSec = payload?.retryAfterSec;
    this.payload = payload;
  }
}

type ApiRequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  accessToken?: string;
  signal?: AbortSignal;
};

const defaultApiBase = "/api";
const apiBase = ((import.meta.env.VITE_API_BASE_URL as string | undefined) || defaultApiBase).replace(/\/+$/, "");

export function getApiBaseForDebug(): string {
  return apiBase;
}

function buildApiUrl(path: string): string {
  const normalizedPath = path.replace(/^\/+/, "");
  return `${apiBase}/${normalizedPath}`;
}

function isJsonResponse(response: Response): boolean {
  return response.headers.get("content-type")?.includes("application/json") ?? false;
}

async function parseErrorPayload(response: Response): Promise<ApiErrorPayload | undefined> {
  if (!isJsonResponse(response)) {
    return undefined;
  }

  try {
    const body = await response.json();
    if (body && typeof body === "object") {
      const payload = body as Record<string, unknown>;
      const detail = payload.detail;

      if (detail && typeof detail === "object") {
        const detailObj = detail as Record<string, unknown>;
        return {
          code: typeof detailObj.code === "string" ? detailObj.code : undefined,
          message: typeof detailObj.message === "string" ? detailObj.message : undefined,
          retryAfterSec: typeof detailObj.retryAfterSec === "number" ? detailObj.retryAfterSec : undefined,
          detail,
        };
      }

      return {
        code: typeof payload.code === "string" ? payload.code : undefined,
        message: typeof payload.message === "string" ? payload.message : undefined,
        retryAfterSec: typeof payload.retryAfterSec === "number" ? payload.retryAfterSec : undefined,
        detail,
      };
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function messageForError(response: Response, payload?: ApiErrorPayload): string {
  switch (payload?.code) {
    case "ERR_RATE_LIMIT":
      return "操作过于频繁，请稍后再试";
    case "ERR_UNAUTHORIZED":
      return "验证码不正确或登录已失效";
    case "ERR_RESOURCE_GONE":
      return "验证码已过期，请重新获取";
    case "ERR_PHONE_BLACKLISTED":
      return "该手机号暂时无法注册或登录";
    case "TEACHER_LINK_NOT_FOUND":
      return "老师链接不存在";
    case "TEACHER_LINK_UNAVAILABLE":
      return "老师链接已失效";
    case "TEACHER_SELF_BIND_NOT_ALLOWED":
      return "老师账号不能绑定为学生";
    case "TEACHER_BIND_FAILED":
      return "绑定失败，请稍后重试";
    case "TEACHER_STUDENT_NOT_BOUND":
      return "请先完成老师链接绑定后再上传";
    case "TEACHER_SELF_RUN_NOT_ALLOWED":
      return "老师账号不能作为学生提交分析";
    case "ERR_FILE_TOO_LARGE":
      return "视频文件过大，请裁剪后再上传";
    case "ERR_STS_FAILED":
    case "ERR_COS_CONFIG":
      return "云端上传凭证获取失败，请稍后重试";
    case "ERR_COS_OBJECT_NOT_FOUND":
      return "云端视频不存在，请重新上传";
    case "ERR_INVALID_TECH_TYPE":
      return "请选择有效动作类型";
    case "ERR_QUOTA_INSUFFICIENT":
      return "老师 AI教练额度不足";
    case "ERR_QUOTA_EXCEEDED":
    case "ERR_QUOTA_NOT_FOUND":
      return "老师下载流量额度不足";
    case "RESULT_NOT_READY":
      return "分析结果还未生成，请稍后再试";
    case "GLB_OBJECT_NOT_FOUND":
      return "模型文件不存在，请稍后再试";
    case "RESULT_ASSET_NOT_FOUND":
      return "结果图片或原视频不存在，请稍后再试";
    case "NO_AUTODL_NODE":
      return "AI教练服务暂时不可用，请稍后再试";
    case "TEACHER_WEB_RUN_FAILED":
      return "AI教练任务创建失败，请稍后重试";
    case "TEACHER_ACCESS_REQUIRED":
      return "当前账号不是教师网页端老师账号";
    case "TEACHER_STUDENT_NOT_FOUND":
      return "没有找到该学生记录";
    default:
      return `请求失败 (${response.status})`;
  }
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const headers = new Headers();
  headers.set("Accept", "application/json");
  headers.set("Accept-Language", "zh-Hans");
  headers.set("X-App-Language", "zh-Hans");

  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  if (options.accessToken) {
    headers.set("Authorization", `Bearer ${options.accessToken}`);
  }

  const requestUrl = buildApiUrl(path);
  let response: Response;

  try {
    response = await fetch(requestUrl, {
      method: options.method ?? "GET",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    const message = error instanceof Error && error.name === "AbortError" ? "请求已取消" : "网络请求失败，请检查网络后重试";
    throw new ApiError(0, message, {
      message,
      detail: {
        apiBase,
      },
    });
  }

  if (!response.ok) {
    const payload = await parseErrorPayload(response);
    throw new ApiError(response.status, messageForError(response, payload), payload);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!isJsonResponse(response)) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
